/**
 * Config file
 * @summary This file should contain all the central configuration variables for the whole project.
 * @howToUse Define the general name of the variable, map that name on the LOCAL, DEV, RC, PROD sections to map the right value from Doppler
 * @return Config JS standard object
 */

interface ConfigProps {
  AUTH0_DOMAIN: string
  AUTH0_CLIENTID: string
  AUTH0_AUDIENCE: string
  PAYMENTS_SERVICE_URL: string
  RESIDENT_SERVICE_URL: string
  DMS_SERVICE_URL: string
  MAINTENANCE_SERVICE_URL: string
  POSTHOG_PROJECT_KEY: string
  UTILITY_CLOSET_URL: string
  LAUNCH_DARKLY_KEY: string
}

// /////////////////////////////////
// Base config
// /////////////////////////////////

const CONFIGS: Record<string, ConfigProps> = {
  [import.meta.env.VITE_LOCAL_DOMAIN]: {
    AUTH0_DOMAIN: import.meta.env.VITE_AUTH_ID_LOCAL,
    AUTH0_CLIENTID: import.meta.env.VITE_CLIENT_ID_LOCAL,
    AUTH0_AUDIENCE: import.meta.env.VITE_AUDIENCE_LOCAL,
    PAYMENTS_SERVICE_URL: import.meta.env.VITE_PAYMENT_SERVICE_BASE_URL_LOCAL,
    RESIDENT_SERVICE_URL: import.meta.env.VITE_RESIDENT_SERVICE_URL_LOCAL,
    DMS_SERVICE_URL: import.meta.env.VITE_DMS_API_URL_LOCAL,
    MAINTENANCE_SERVICE_URL: import.meta.env.VITE_MAINT_SERVICE_URL_LOCAL,
    POSTHOG_PROJECT_KEY: import.meta.env.VITE_POSTHOG_PROJECT_KEY_LOCAL,
    UTILITY_CLOSET_URL: import.meta.env.VITE_UTILITY_CLOSET_URL_LOCAL,
    LAUNCH_DARKLY_KEY: import.meta.env.VITE_LAUNCH_DARKLY_KEY_LOCAL,
  },
  [import.meta.env.VITE_DEV_DOMAIN]: {
    AUTH0_DOMAIN: import.meta.env.VITE_AUTH_ID_DEV,
    AUTH0_CLIENTID: import.meta.env.VITE_CLIENT_ID_DEV,
    AUTH0_AUDIENCE: import.meta.env.VITE_AUDIENCE_DEV,
    PAYMENTS_SERVICE_URL: import.meta.env.VITE_PAYMENT_SERVICE_BASE_URL_DEV,
    RESIDENT_SERVICE_URL: import.meta.env.VITE_RESIDENT_SERVICE_URL_DEV,
    DMS_SERVICE_URL: import.meta.env.VITE_DMS_API_URL_DEV,
    MAINTENANCE_SERVICE_URL: import.meta.env.VITE_MAINT_SERVICE_URL_DEV,
    POSTHOG_PROJECT_KEY: import.meta.env.VITE_POSTHOG_PROJECT_KEY_DEV,
    UTILITY_CLOSET_URL: import.meta.env.VITE_UTILITY_CLOSET_URL_DEV,
    LAUNCH_DARKLY_KEY: import.meta.env.VITE_LAUNCH_DARKLY_KEY_DEV,
  },
  [import.meta.env.VITE_RC_DOMAIN]: {
    AUTH0_DOMAIN: import.meta.env.VITE_AUTH_ID_RC,
    AUTH0_CLIENTID: import.meta.env.VITE_CLIENT_ID_RC,
    AUTH0_AUDIENCE: import.meta.env.VITE_AUDIENCE_RC,
    PAYMENTS_SERVICE_URL: import.meta.env.VITE_PAYMENT_SERVICE_BASE_URL_RC,
    RESIDENT_SERVICE_URL: import.meta.env.VITE_RESIDENT_SERVICE_URL_RC,
    DMS_SERVICE_URL: import.meta.env.VITE_DMS_API_URL_RC,
    MAINTENANCE_SERVICE_URL: import.meta.env.VITE_MAINT_SERVICE_URL_RC,
    POSTHOG_PROJECT_KEY: import.meta.env.VITE_POSTHOG_PROJECT_KEY_RC,
    UTILITY_CLOSET_URL: import.meta.env.VITE_UTILITY_CLOSET_URL_RC,
    LAUNCH_DARKLY_KEY: import.meta.env.VITE_LAUNCH_DARKLY_KEY_RC,
  },
  [import.meta.env.VITE_PROD_DOMAIN]: {
    AUTH0_DOMAIN: import.meta.env.VITE_AUTH_ID_PROD,
    AUTH0_CLIENTID: import.meta.env.VITE_CLIENT_ID_PROD,
    AUTH0_AUDIENCE: import.meta.env.VITE_AUDIENCE_PROD,
    PAYMENTS_SERVICE_URL: import.meta.env.VITE_PAYMENT_SERVICE_BASE_URL_PROD,
    RESIDENT_SERVICE_URL: import.meta.env.VITE_RESIDENT_SERVICE_URL_PROD,
    DMS_SERVICE_URL: import.meta.env.VITE_DMS_API_URL_PROD,
    MAINTENANCE_SERVICE_URL: import.meta.env.VITE_MAINT_SERVICE_URL_PROD,
    POSTHOG_PROJECT_KEY: import.meta.env.VITE_POSTHOG_PROJECT_KEY_PROD,
    UTILITY_CLOSET_URL: import.meta.env.VITE_UTILITY_CLOSET_URL_PROD,
    LAUNCH_DARKLY_KEY: import.meta.env.VITE_LAUNCH_DARKLY_KEY_PROD,
  },
}

// /////////////////////////////////
// Generate environment specific config
// /////////////////////////////////

/**
 * PR ephemeral frontends are hosted on Cloud Run with dynamic origins
 * (e.g. https://pr-<sha>-dashboard-renter-dev-….run.app). Those hosts are
 * not in CONFIGS, so without this check they incorrectly fall back to LOCAL
 * and call http://localhost:4021 from the browser.
 */
export const isEphemeralPreviewOrigin = (origin: string): boolean => {
  try {
    const { hostname } = new URL(origin)
    return hostname.startsWith('pr-') && hostname.endsWith('.run.app')
  } catch {
    return false
  }
}

const resolveConfig = (origin: string): ConfigProps => {
  if (CONFIGS[origin]) {
    return CONFIGS[origin]
  }

  if (isEphemeralPreviewOrigin(origin)) {
    return CONFIGS[import.meta.env.VITE_DEV_DOMAIN]
  }

  return CONFIGS[import.meta.env.VITE_LOCAL_DOMAIN]
}

const VITE_CONFIG = resolveConfig(window.location.origin)

// /////////////////////////////////
// Exposed config object
// /////////////////////////////////
const CONFIG = {
  ...VITE_CONFIG,
  IS_DEV:
    window.location.origin === import.meta.env.VITE_DEV_DOMAIN ||
    isEphemeralPreviewOrigin(window.location.origin),
  IS_RC: window.location.origin === import.meta.env.VITE_RC_DOMAIN,
  IS_PROD: window.location.origin === import.meta.env.VITE_PROD_DOMAIN,
  POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST,
}

export default CONFIG
